import { Bell, X } from 'lucide-react';
import React, { useEffect, useRef, useState } from 'react';
import { NotificationCategory, useNotifications } from './NotificationContext';
import './NotificationBell.css';

type Tab = 'ALL' | NotificationCategory;
const TABS: Tab[] = ['ALL', 'USERS', 'SYSTEM'];

export const NotificationBell: React.FC = () => {
    const { notifications, unreadCount, connected, error, markAllRead } = useNotifications();
    const [open, setOpen] = useState(false);
    const [activeTab, setActiveTab] = useState<Tab>('ALL');
    const ref = useRef<HTMLDivElement>(null);

    // Close on outside click
    useEffect(() => {
        const handler = (e: MouseEvent) => {
            if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, []);

    // Close on Escape
    useEffect(() => {
        const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
        document.addEventListener('keydown', handler);
        return () => document.removeEventListener('keydown', handler);
    }, []);

    const filtered = activeTab === 'ALL'
        ? [...notifications].reverse()
        : [...notifications].filter(n => n.category === activeTab).reverse();

    const tabUnread = (tab: Exclude<Tab, 'ALL'>) =>
        notifications.filter(n => n.category === tab && !n.read).length;

    return (
        <div ref={ref} className="notif-bell">
            <button
                className={`notif-bell__trigger ${open ? 'open' : ''}`}
                onClick={() => setOpen(o => !o)}
                aria-label="Уведомления"
                aria-expanded={open}
            >
                <Bell size={14} />
                {unreadCount > 0 && (
                    <span className="notif-bell__badge">
                        {unreadCount > 99 ? '99+' : unreadCount}
                    </span>
                )}
            </button>

            {open && (
                <div className="notif-panel" role="dialog" aria-label="Панель уведомлений">
                    {/* Header */}
                    <div className="notif-panel__header">
                        <div className="notif-panel__title-row">
                            <span className="notif-panel__title">Notifications</span>
                            <div className="notif-panel__header-right">
                                <span className={`notif-panel__conn ${connected ? 'connected' : 'disconnected'}`}>
                                    {connected ? '● live' : '○ off'}
                                </span>
                                <button
                                    className="notif-panel__mark-read"
                                    onClick={markAllRead}
                                    disabled={unreadCount === 0}
                                >
                                    mark read
                                </button>
                                <button
                                    className="notif-panel__close"
                                    onClick={() => setOpen(false)}
                                    aria-label="Закрыть"
                                >
                                    <X size={12} />
                                </button>
                            </div>
                        </div>

                        {/* Tabs */}
                        <div className="notif-panel__tabs">
                            {TABS.map(tab => (
                                <button
                                    key={tab}
                                    className={`notif-panel__tab ${activeTab === tab ? 'active' : ''}`}
                                    onClick={() => setActiveTab(tab)}
                                >
                                    {tab}
                                    {tab !== 'ALL' && tabUnread(tab) > 0 && (
                                        <span className="notif-tab__dot" />
                                    )}
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* Error */}
                    {error && <div className="notif-panel__error">{error}</div>}

                    {/* List */}
                    <div className="notif-panel__list">
                        {filtered.length === 0 ? (
                            <div className="notif-panel__empty">
                                {activeTab === 'SYSTEM' ? 'No system events' :
                                 activeTab === 'USERS'  ? 'No user notifications' :
                                                          'No notifications yet'}
                            </div>
                        ) : (
                            filtered.map(n => (
                                <div key={n.id} className={`notif-item ${n.read ? 'read' : 'unread'}`}>
                                    <div className="notif-item__meta">
                                        <span className="notif-item__cat">{n.category}</span>
                                        <span className="notif-item__time">{n.at}</span>
                                    </div>
                                    <p className="notif-item__body">{n.body}</p>
                                </div>
                            ))
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};
